import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FolderOpen, Trash2, CalendarRange, Clock, Mail, UserCircle2 } from "lucide-react";

const fmtDate = (s) => (s ? s.split("-").reverse().join("/") : "");
const fmtSentAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = d.toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time}`;
};

// ---- Box Orbit storico (ricostruito dallo snapshot, NON dalle work_entries) ----
function EntriesTable({ entries }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            <th className="px-3 py-2 font-medium">Educatore</th>
            <th className="px-3 py-2 font-medium">Data</th>
            <th className="px-3 py-2 font-medium">Orario</th>
            <th className="px-3 py-2 font-medium text-right">Ore</th>
          </tr>
        </thead>
        <tbody>
          {(entries || []).map((e, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-2 text-slate-700">{e.educatore}</td>
              <td className="px-3 py-2 text-slate-700">{fmtDate(e.date)}</td>
              <td className="px-3 py-2 text-slate-700">{e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : ""}</td>
              <td className="px-3 py-2 text-slate-700 text-right">{e.hours_label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssistenzaBox({ snap }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="archive-box-assistenza">
      <div className="px-4 py-3 bg-[#2F5D62]">
        <p className="font-heading font-bold text-white">{snap.school_name}</p>
        {snap.plesso && <p className="text-xs text-white/70">Plesso/Istituto: {snap.plesso}</p>}
      </div>
      <div className="p-4 space-y-4">
        {(snap.users || []).map((u) => (
          <div key={u.user_id} className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 bg-[#E8EFF0] font-semibold text-slate-800 text-sm">{u.user_name}</div>
            <EntriesTable entries={u.entries} />
            <div className="px-3 py-2 bg-[#F1F5F4] text-sm font-semibold text-slate-800 text-right">
              Totale {u.user_name}: {u.total_hours_label} h
            </div>
          </div>
        ))}
        <div className="rounded-xl bg-[#DDEAE7] px-3 py-2.5 text-sm font-bold text-[#1F3A3D] text-right">
          Totale assistenza scuola nel periodo: {snap.total_hours_label} h
        </div>
      </div>
    </div>
  );
}

function AdmBox({ snap }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="archive-box-adm">
      <div className="px-4 py-3 bg-[#3A4A7A]">
        <p className="font-heading font-bold text-white">{snap.user_name}</p>
      </div>
      <div className="p-4">
        <EntriesTable entries={snap.entries} />
        <div className="mt-3 rounded-xl bg-[#E3E8F4] px-3 py-2 text-sm font-bold text-[#20305C] text-right">
          Totale {snap.user_name}: {snap.total_hours_label} h
        </div>
      </div>
    </div>
  );
}

export function ExternalCheckArchive({ type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);     // voce da visualizzare
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const accent = type === "adm" ? "#3A4A7A" : "#2F5D62";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/office/external-checks/archive?type=${type}`);
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore caricamento archivio"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const doDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      await api.delete(`/office/external-checks/archive/${toDelete.id}`);
      toast.success("Voce eliminata dall'archivio");
      setToDelete(null); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eliminazione non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`external-archive-${type}`}>
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="rounded-xl bg-white border border-slate-200 h-24 animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center text-slate-500 text-sm" data-testid={`external-archive-${type}-empty`}>
            Nessun check in archivio.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid={`external-archive-${type}-list`}>
          {items.map((it) => (
            <Card key={it.id} className="rounded-xl border-slate-200 shadow-sm" data-testid={`external-archive-item-${it.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-800">{it.recipient_name}</p>
                      <Badge className="border-0 text-[10px] text-white" style={{ backgroundColor: accent }}>{it.appalto_nome}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5">
                      <CalendarRange className="h-3.5 w-3.5" /> Periodo: {fmtDate(it.date_from)} – {fmtDate(it.date_to)}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Clock className="h-3.5 w-3.5" /> Inviato il {fmtSentAt(it.sent_at)}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Mail className="h-3.5 w-3.5" /> {it.recipient_email}
                    </p>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                      <UserCircle2 className="h-3.5 w-3.5" /> Inviato da {it.sent_by_name}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setOpen(it)} data-testid={`external-archive-open-${it.id}`} className="rounded-lg">
                      <FolderOpen className="h-4 w-4 mr-1.5" /> Apri
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setToDelete(it)} data-testid={`external-archive-delete-${it.id}`}
                      className="text-slate-400 hover:text-destructive hover:bg-destructive/5">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="external-archive-view">
          <DialogHeader>
            <DialogTitle className="font-heading">{open?.recipient_name}</DialogTitle>
            <DialogDescription>
              Periodo {fmtDate(open?.date_from)} – {fmtDate(open?.date_to)} · Inviato il {fmtSentAt(open?.sent_at)} a {open?.recipient_email}
            </DialogDescription>
          </DialogHeader>
          {open && (type === "adm" ? <AdmBox snap={open.snapshot} /> : <AssistenzaBox snap={open.snapshot} />)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="external-archive-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa voce dall'Archivio?</AlertDialogTitle>
            <AlertDialogDescription>
              Viene rimossa esclusivamente la memoria archivistica dell'invio. Nessun dato operativo verrà modificato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="external-archive-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={busy} data-testid="external-archive-delete-confirm"
              className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
